import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import EntityUserRoleAssignmentsPage from '@features/user/pages/EntityUserRoleAssignmentsPage';
import EntityScopePicker, {
  EntityScopePickerChange,
} from '@features/entity/components/EntityScopePicker';
import { useEntityContext } from '@features/entity/hooks/usePermissions';
import {
  ENTITY_SCOPE_TOGGLE_CLASS,
  encodeEntityScopeDestination,
  formatEntityScopeLabel,
} from '@features/entity/constants/entityScopePicker';

interface ActiveScope {
  scope: string;
  scopeRefId: number;
  name?: string;
}

const DistrictUserRoleAssignmentsPage: React.FC = () => {
  const { districtId } = useParams<{ districtId: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const sid = Number(districtId);
  const { context } = useEntityContext();

  const ownLabel = useMemo(() => {
    const name = context?.scopeName?.trim() || 'This District';
    return formatEntityScopeLabel('DISTRICT', name.replace(/^District:\s*/i, ''));
  }, [context?.scopeName]);

  const [active, setActive] = useState<ActiveScope>({
    scope: 'DISTRICT',
    scopeRefId: sid,
    name: ownLabel,
  });

  useEffect(() => {
    if (!(sid > 0)) return;
    setActive((prev) =>
      prev.scope === 'DISTRICT' && prev.scopeRefId !== sid
        ? { scope: 'DISTRICT', scopeRefId: sid, name: ownLabel }
        : prev.scope === 'DISTRICT' && !prev.name
          ? { ...prev, name: ownLabel }
          : prev
    );
  }, [sid, ownLabel]);

  const destinationValue = useMemo(
    () => encodeEntityScopeDestination(active.scope, active.scopeRefId),
    [active.scope, active.scopeRefId]
  );

  const handleScopeChange = (next: EntityScopePickerChange) => {
    // Clear list URL state so remounted useSearch does not inherit filters from the prior entity.
    if (searchParams.toString()) {
      setSearchParams({}, { replace: true });
    }
    setActive({
      scope: next.scope,
      scopeRefId: next.scopeRefId,
      name: next.name,
    });
  };

  const scopeSelector =
    sid > 0 ? (
      <EntityScopePicker
        ownScopeValue="D"
        ownScopeRefId={sid}
        ownLabel={ownLabel}
        value={destinationValue}
        onChange={handleScopeChange}
        readOnlyWhenNoChildren={false}
        toggleClassName={ENTITY_SCOPE_TOGGLE_CLASS}
        testId="district-user-role-assignments-scope-picker"
      />
    ) : null;

  const listScope = (active.scope.toUpperCase() || 'DISTRICT') as
    | 'NATION'
    | 'DISTRICT'
    | 'CHURCH';

  return (
    <div key={`${active.scope}:${active.scopeRefId}`}>
      <EntityUserRoleAssignmentsPage
        scope={listScope}
        scopeRefId={active.scopeRefId}
        title="User role assignments"
        subtitle="Manage role assignments at this district and its churches"
        selectedEntityDisplayName={active.name ?? ownLabel}
        leadingControl={scopeSelector}
        testId="district-user-role-assignments-search"
      />
    </div>
  );
};

export default DistrictUserRoleAssignmentsPage;
